import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";

export default function ResetPasswordPage() {
  usePageMeta("Set New Password | MacWay Estate Services", "Set a new MacWay dashboard password.");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[70vh] bg-sand flex items-center justify-center py-16 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-linen p-8 md:p-10">
        {!token ? (
          <div className="text-center" data-testid="reset-missing-token">
            <ShieldAlert className="w-12 h-12 text-brass mx-auto" />
            <h1 className="mt-5 text-3xl font-medium text-ink">This link is incomplete</h1>
            <p className="mt-3 text-sm text-ink/65">The reset link appears to be missing its token. Please request a fresh one.</p>
            <Link to="/admin/forgot-password" className="btn-primary mt-8 w-full">Request New Link</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-forest mx-auto" />
            <h1 className="mt-5 text-3xl font-medium text-ink">Password updated</h1>
            <p className="mt-3 text-sm text-ink/65">You can now sign in with your new password.</p>
            <Link to="/admin/login" className="btn-primary mt-8 w-full" data-testid="reset-go-login">
              Sign In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-medium text-ink text-center" data-testid="reset-heading">Choose a new password</h1>
            <p className="mt-3 text-sm text-ink/60 text-center">At least 8 characters. This link works once and expires after 1 hour.</p>
            <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="reset-password-form">
              <div>
                <label htmlFor="reset-password" className="form-label">New password</label>
                <input
                  id="reset-password"
                  type="password"
                  className="form-input"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label htmlFor="reset-confirm" className="form-label">Confirm new password</label>
                <input
                  id="reset-confirm"
                  type="password"
                  className="form-input"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                />
              </div>
              {error && (
                <p className="text-sm font-semibold text-red-700 bg-red-50 border border-red-200 px-4 py-3" role="alert" data-testid="reset-error">
                  {error}
                </p>
              )}
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="reset-submit">
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Updating…
                  </>
                ) : (
                  "Set New Password"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
